import {
  useCallback, useMemo, useState
} from "react";
import clsx from "clsx";
import {
  createColumnHelper,
  getCoreRowModel, getSortedRowModel, SortingState, useReactTable
} from "@tanstack/react-table";
import { BusinessSubscription, SubscriptionInfo } from "lens-platform-sdk";
import { action } from "@k8slens/lds-icons";

import { trimSubscriptionId } from "src/services/trimSubscriptionId";
import { getPlanName } from "src/services/plans";
import { openUrl } from "src/utils/url";
import { getAccountUrl } from "src/services/billing/billingUrl";

import Table from "src/components/Table/Table";
import { ContextMenu, ContextMenuItem } from "src/components/ContextMenu/ContextMenu";
import { ContextMenuIcon } from "src/components/Icon/Navigation";

import { dateToString } from "src/utils/date";
import styles from "./BillingDetails.module.css";

const subscriptionColumnHelper = createColumnHelper<SubscriptionInfo | BusinessSubscription>();
const { DeleteIcon } = action;

export interface SubscriptionsProps {
  subscriptions: SubscriptionInfo[] | BusinessSubscription[];
  billingPageToken: string | null;
  loading: boolean;
}

export const Subscriptions = ({ subscriptions, billingPageToken: token, loading }: SubscriptionsProps) => {
  const [subscriptionsSorting, setSubscriptionsSorting] = useState<SortingState>([]);

  const cancelSubscription = useCallback(() => {
    if (!token) {
      return;
    }

    const accountUrl = getAccountUrl(token);

    if (!accountUrl) {
      return;
    }
    openUrl(accountUrl, true);
  }, [token]);

  const subscriptionsColumns = useMemo(() => [
    subscriptionColumnHelper.accessor("id", {
      header: () => <span>Subscription ID</span>,
      cell: ({ getValue }) => {
        const id = getValue();

        if (!id) {
          return null;
        }

        return (
          <div className={styles.subscriptionId}>{trimSubscriptionId(id)}</div>
        );
      },
      meta: { primary: true }
    }),
    subscriptionColumnHelper.accessor("planCode", {
      header: () => <span className="subscriptionsPlan">Plan</span>,
      cell: ({ getValue }) => (
        <div className="subscriptionsPlanValue">{getPlanName(getValue())}</div>
      )
    }),
    subscriptionColumnHelper.accessor("currentPeriodEndsAt", {
      header: () => <span>Next Renewal Date</span>,
      cell: ({ getValue }) => <span>{dateToString(new Date(getValue() ?? ""))}</span>
    }),
    subscriptionColumnHelper.accessor("seats", {
      header: () => <span>Seats</span>,
      cell: ({ getValue }) => (
        <div>{getValue()}</div>
      ),
      meta: { primary: true }
    }),
    subscriptionColumnHelper.accessor("state", {
      header: () => <span>Status</span>,
      cell: ({ getValue }) => (
        <div className={clsx(styles.status, styles[getValue()] ?? styles.defaultState)}>{getValue()}</div>
      )
    }),
    subscriptionColumnHelper.accessor("id", {
      id: "actions",
      header: () => <span />,
      enableSorting: false,
      cell: ({ row }) => {
        const subscription = row.original;

        return (
          <div>
            <ContextMenu toggleIcon={ContextMenuIcon} toggleButtonProps={{ title: "Toggle subscription context menu" }}>
              <ContextMenuItem disabled={subscription.state === "canceled"} label="Cancel" icon={DeleteIcon} onClick={() => cancelSubscription()} />
            </ContextMenu>
          </div>
        );
      }
    })
  ], [cancelSubscription]);

  const subscriptionsTable = useReactTable({
    data: subscriptions,
    columns: subscriptionsColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSubscriptionsSorting,
    state: {
      sorting: subscriptionsSorting
    }
  });
  const subscriptionsTableRowModel = subscriptionsTable.getRowModel();
  const subscriptionsTableRows = useMemo(() => subscriptionsTableRowModel.rows, [subscriptionsTableRowModel]);

  return (
    <Table<BusinessSubscription | SubscriptionInfo>
      caption="Subscriptions"
      rows={subscriptionsTableRows}
      columns={subscriptionsTable.getAllColumns()}
      headerGroups={subscriptionsTable.getHeaderGroups()}
      noDataText="No subscriptions founded"
      className={styles.table}
      loading={loading}
      headerGroupProps={{
        cellClassName: styles.tableHeader
      }}
      rowProps={{
        className: styles.tableRow,
        cellClassName: styles.tableCell
      }}
    />
  );
};
